import type { RefObject } from "react";
import { useRef, useState, useCallback } from "react";

/**
 * Результат работы хука useTimeredAnimation
 * @property {boolean} isTimeredActive - Флаг активности анимации
 * @property {() => void} startAnimation - Функция запуска анимации
 * @property {() => void} stopAnimation - Функция принудительной остановки анимации
 */
interface UseTimeredAnimationResult {
    isTimeredActive: boolean;
    startAnimation: () => void;
    stopAnimation: () => void;
}

/**
 * Хук для управления анимацией с таймером
 * @param {RefObject<HTMLElement>} elementRef - Реф на DOM-элемент для анимации
 * @param {number} duration - Длительность анимации в миллисекундах
 * @returns {UseTimeredAnimationResult} - Объект с состоянием и методами управления анимацией
 */
export const useTimeredAnimation = (
    elementRef: RefObject<HTMLElement>,
    duration: number,
): UseTimeredAnimationResult => {
    const [isTimeredActive, setIsTimeredActive] = useState(false);
    const originalWidthRef = useRef<string>("");
    const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);

    /**
     * Запускает анимацию с фиксацией текущей ширины элемента
     */
    const startAnimation = useCallback(() => {
        const element = elementRef.current;
        if (!element) return;
        originalWidthRef.current = element.style.width;
        const hasCustomWidth =
            originalWidthRef.current && originalWidthRef.current !== "auto";
        if (!hasCustomWidth) {
            const elementWidth = element.getBoundingClientRect().width;
            element.style.width = `${elementWidth}px`;
        }
        setIsTimeredActive(true);
        timerRef.current = setTimeout(() => {
            setIsTimeredActive(false);
            element.style.width = originalWidthRef.current;
            clearTimeout(timerRef.current);
        }, duration);
    }, [elementRef, duration]);

    /**
     * Принудительно останавливает анимацию и восстанавливает исходные параметры
     */
    const stopAnimation = useCallback(() => {
        const element = elementRef.current;
        if (!element) return;
        setIsTimeredActive(false);
        element.style.width = originalWidthRef.current;
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
    }, [elementRef]);

    return { isTimeredActive, startAnimation, stopAnimation };
};
